import { useId, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { BellRing, Loader2, MessageSquareText, Pencil, X } from "lucide-react";
import { useItem, useUpdateItem } from "@/api/hooks";
import { ApiClientError } from "@/api/client";
import type { Item, ItemCondition } from "@/api/types";
import { Button } from "@/components/ui/button";

const CONDITION_LABELS: Record<ItemCondition, string> = {
  new: "Neuf",
  good: "Bon état",
  fair: "État moyen",
  poor: "Mauvais état",
};

/** Mirrors the TEXT column of items.notes; a generous bound keeps the form usable. */
const NOTES_MAX_LENGTH = 2000;

const dateFormatter = new Intl.DateTimeFormat("fr-FR", { dateStyle: "long" });
const dateTimeFormatter = new Intl.DateTimeFormat("fr-FR", {
  dateStyle: "long",
  timeStyle: "short",
});
const priceFormatter = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });

/**
 * Item record (fiche exemplaire): shows the copy's details, including when it
 * was created, and lets staff edit its internal comment. Mounted only while
 * open, so each opening reads the item fresh from the API.
 */
export function ItemDetailDialog({
  itemId,
  onClose,
  onUpdated,
}: {
  itemId: string;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const { data: item, isLoading, error } = useItem(itemId);

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="animate-fade-in fixed inset-0 z-40 bg-gray-950/40 backdrop-blur-sm" />
        <Dialog.Content className="animate-fade-in fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col bg-white shadow-2xl dark:bg-gray-900">
          <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5 dark:border-gray-700">
            <div className="min-w-0">
              <p className="text-xs font-medium tracking-wide text-gray-500 uppercase dark:text-gray-400">
                Fiche exemplaire
              </p>
              <Dialog.Title className="mt-1 truncate font-mono text-lg font-semibold text-gray-900 dark:text-white">
                {item?.barcode ?? "Exemplaire"}
              </Dialog.Title>
              <Dialog.Description className="mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400">
                {item ? item.biblioRecord.title : "Détails de l'exemplaire"}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Fermer">
                <X aria-hidden />
              </Button>
            </Dialog.Close>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
            {isLoading && (
              <p
                role="status"
                className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400"
              >
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Chargement de la fiche…
              </p>
            )}

            {!isLoading && (error || !item) && (
              <p
                role="alert"
                className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
              >
                Impossible de charger cet exemplaire.
              </p>
            )}

            {item && (
              <div className="space-y-8">
                <ItemFacts item={item} />
                <ItemNotes item={item} onSaved={onUpdated} />
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ItemFacts({ item }: { item: Item }) {
  const facts: Array<{ label: string; value: string }> = [
    { label: "Statut", value: item.statusLabel },
    { label: "Localisation", value: item.location.name },
    { label: "Cote", value: item.callNumber ?? "—" },
    { label: "État", value: CONDITION_LABELS[item.condition] },
    {
      label: "Acquis le",
      value: item.acquisitionDate ? dateFormatter.format(new Date(item.acquisitionDate)) : "—",
    },
    {
      label: "Prix d'acquisition",
      value: item.acquisitionPrice ? priceFormatter.format(Number(item.acquisitionPrice)) : "—",
    },
    {
      label: "Circulation",
      value: [
        item.loanable ? "Prêtable" : "Exclu du prêt",
        item.reservable ? "réservable" : "non réservable",
      ].join(", "),
    },
  ];

  return (
    <section aria-label="Informations">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
        {facts.map(({ label, value }) => (
          <div key={label}>
            <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
            <dd className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white">{value}</dd>
          </div>
        ))}
      </dl>

      <dl className="mt-6 space-y-1 border-t border-gray-100 pt-4 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
        <div className="flex gap-1.5">
          <dt>Créé le</dt>
          <dd className="font-medium text-gray-700 dark:text-gray-300">
            <time dateTime={item.createdAt}>
              {dateTimeFormatter.format(new Date(item.createdAt))}
            </time>
          </dd>
        </div>
        <div className="flex gap-1.5">
          <dt>Modifié le</dt>
          <dd className="font-medium text-gray-700 dark:text-gray-300">
            <time dateTime={item.updatedAt}>
              {dateTimeFormatter.format(new Date(item.updatedAt))}
            </time>
          </dd>
        </div>
      </dl>
    </section>
  );
}

function ItemNotes({ item, onSaved }: { item: Item; onSaved: () => void }) {
  const fieldId = useId();
  const updateItem = useUpdateItem(item.id);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(item.notes ?? "");
  const [showAtCheckin, setShowAtCheckin] = useState(item.notesShownAtCheckin);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const startEditing = () => {
    setDraft(item.notes ?? "");
    setShowAtCheckin(item.notesShownAtCheckin);
    setError(null);
    setSaved(false);
    setIsEditing(true);
  };

  const save = async () => {
    setError(null);
    try {
      // An empty string clears the comment server-side
      await updateItem.mutateAsync({
        notes: draft.trim(),
        notesShownAtCheckin: showAtCheckin,
      });
      setIsEditing(false);
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiClientError && err.status === 403
          ? "Vous n'avez pas les droits pour modifier cet exemplaire."
          : "L'enregistrement du commentaire a échoué. Réessayez."
      );
    }
  };

  return (
    <section aria-labelledby={`${fieldId}-title`}>
      <div className="flex items-center justify-between gap-3">
        <h3
          id={`${fieldId}-title`}
          className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white"
        >
          <MessageSquareText className="h-4 w-4 text-gray-400" aria-hidden />
          Commentaire
        </h3>
        {!isEditing && (
          <Button variant="ghost" size="sm" onClick={startEditing}>
            <Pencil aria-hidden />
            {item.notes ? "Modifier" : "Ajouter"}
          </Button>
        )}
      </div>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Interne à l'équipe : jamais affiché sur le portail des lecteurs.
      </p>

      {isEditing ? (
        <form
          className="mt-3 space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <label htmlFor={fieldId} className="sr-only">
            Texte du commentaire
          </label>
          <textarea
            id={fieldId}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            rows={5}
            maxLength={NOTES_MAX_LENGTH}
            placeholder="Ex. : jaquette abîmée, CD manquant, don de M. X…"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            autoFocus
          />
          <label className="flex cursor-pointer items-start gap-2.5 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={showAtCheckin}
              onChange={(event) => {
                setShowAtCheckin(event.target.checked);
              }}
              aria-describedby={`${fieldId}-checkin-hint`}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
            />
            <span>
              Afficher ce commentaire lors du retour
              <span
                id={`${fieldId}-checkin-hint`}
                className="block text-xs text-gray-500 dark:text-gray-400"
              >
                L'agent le verra à la banque de prêt au moment du retour de cet exemplaire.
              </span>
            </span>
          </label>
          {error && (
            <p role="alert" className="text-sm text-red-700 dark:text-red-400">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIsEditing(false);
                setError(null);
              }}
              disabled={updateItem.isPending}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={updateItem.isPending}>
              {updateItem.isPending && <Loader2 className="animate-spin" aria-hidden />}
              Enregistrer
            </Button>
          </div>
        </form>
      ) : item.notes ? (
        <>
          <p className="mt-3 rounded-lg bg-gray-50 p-3 text-sm whitespace-pre-line text-gray-800 dark:bg-gray-800 dark:text-gray-200">
            {item.notes}
          </p>
          {item.notesShownAtCheckin && (
            <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-amber-800 dark:text-amber-300">
              <BellRing className="h-3.5 w-3.5" aria-hidden />
              Affiché lors du retour
            </p>
          )}
        </>
      ) : (
        <p className="mt-3 text-sm text-gray-400 italic dark:text-gray-500">Aucun commentaire.</p>
      )}

      <p role="status" className="sr-only">
        {saved ? "Commentaire enregistré." : ""}
      </p>
    </section>
  );
}
